/**
 * DealerRedemptionPage (iter137 — Delivery Honesty)
 *
 * Where dealer staff type the customer's claim ticket to trigger the
 * atomic gift-card mint and dual-channel delivery (SMS + email).
 *
 * Never surfaces the code itself. Delivery state is HONEST — a card
 * is only shown as "sent" if either channel confirmed delivery. SMS
 * that GHL accepted but has not yet confirmed shows as "confirming
 * delivery" (dealer sees the customer being taken care of, but the
 * dealer log will not count it as sent until the poller confirms).
 *
 * If BOTH channels fail, the dealer sees a loud red banner with a
 * Retry button that re-sends the SAME code (never mints a second).
 */
import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { CheckCircle2, AlertCircle, Loader2, Search, Ticket, Clock, MailX, RefreshCw } from "lucide-react";

/** Compose the banner style + icon + copy from per-channel statuses. */
function computeBannerVariant(result) {
  if (!result) return null;
  const sms = result.sms_status || "unknown";
  const email = result.email_status || "unknown";
  if (result.status === "stockout_pending") {
    return { tone: "warn", icon: <AlertCircle size={18} />, title: "Inventory out — customer flagged pending" };
  }
  const anySent = sms === "sent" || email === "sent";
  const anyPending = sms === "pending";
  const bothFailed = sms === "failed" && (email === "failed" || email === "skipped" || email === "suppressed");
  if (anySent) {
    return { tone: "ok", icon: <CheckCircle2 size={18} />, title: "Card delivered" };
  }
  if (anyPending) {
    return { tone: "info", icon: <Clock size={18} />, title: "Card sent — confirming delivery" };
  }
  if (bothFailed) {
    return { tone: "err", icon: <AlertCircle size={18} />, title: "Card NOT sent — delivery failed" };
  }
  return { tone: "info", icon: <Clock size={18} />, title: result.message || "Processing" };
}

function ChannelPill({ label, status }) {
  const styles = {
    sent: "bg-emerald-100 text-emerald-800",
    pending: "bg-amber-100 text-amber-900",
    failed: "bg-red-100 text-red-800",
    skipped: "bg-slate-100 text-slate-600",
    suppressed: "bg-slate-100 text-slate-600",
    unknown: "bg-slate-100 text-slate-600",
  };
  const cls = styles[status] || styles.unknown;
  const short = status === "skipped" ? "no email on file" : status;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold ${cls}`}
          data-testid={`redeem-${label.toLowerCase()}-status`}>
      {label}: {short || "—"}
    </span>
  );
}

const TONE_CLS = {
  ok:   "border-emerald-300 bg-emerald-50 text-emerald-900",
  info: "border-sky-300 bg-sky-50 text-sky-900",
  warn: "border-amber-300 bg-amber-50 text-amber-900",
  err:  "border-red-300 bg-red-50 text-red-900",
};

export default function DealerRedemptionPage() {
  const { apiFetch } = useAuth();
  const [ticket, setTicket] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [retryBusy, setRetryBusy] = useState(false);

  // Resend lookup panel state
  const [resendPhone, setResendPhone] = useState("");
  const [resendName, setResendName] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [resendResult, setResendResult] = useState(null);
  const [resendError, setResendError] = useState(null);

  const submitTicket = async (e) => {
    e?.preventDefault?.();
    if (!ticket.trim()) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await apiFetch("/dealer/gift-cards/redeem", {
        method: "POST",
        data: { claim_ticket: ticket.trim().toUpperCase() },
      });
      setResult(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Redemption failed. Check the ticket and try again.");
    } finally {
      setBusy(false);
    }
  };

  // Retry uses the /dealer/gift-cards/resend endpoint with the SAME
  // customer's phone — sends the same code again without minting new.
  const retryDelivery = async () => {
    if (!result?.customer_phone_masked) return;
    setRetryBusy(true);
    setError(null);
    try {
      // Use the phone or fall back to name (resend endpoint accepts either).
      const res = await apiFetch("/dealer/gift-cards/resend", {
        method: "POST",
        data: { name: result.customer_name },
      });
      setResult(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Retry failed. Try the resend panel below.");
    } finally {
      setRetryBusy(false);
    }
  };

  const submitResend = async (e) => {
    e?.preventDefault?.();
    if (!resendPhone.trim() && !resendName.trim()) {
      setResendError("Enter a phone or a name.");
      return;
    }
    setResendBusy(true);
    setResendError(null);
    setResendResult(null);
    try {
      const res = await apiFetch("/dealer/gift-cards/resend", {
        method: "POST",
        data: {
          phone: resendPhone.trim() || undefined,
          name: resendName.trim() || undefined,
        },
      });
      setResendResult(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setResendError(typeof detail === "string" ? detail : "Resend failed.");
    } finally {
      setResendBusy(false);
    }
  };

  const variant = computeBannerVariant(result);
  const showRetry = variant?.tone === "err" && result?.customer_name;

  const banner = result && variant && (
    <div
      className={`mt-3 rounded-md border p-3 flex items-start gap-2 ${TONE_CLS[variant.tone]}`}
      data-testid="dealer-redeem-result"
      data-tone={variant.tone}
    >
      <div className="mt-0.5 shrink-0">{variant.icon}</div>
      <div className="flex-1">
        <div className="font-semibold text-[13px]">{variant.title}</div>
        <div className="text-[12px] mt-0.5">
          {result.customer_name} · {result.customer_phone_masked}
          {result.customer_email_masked ? ` · ${result.customer_email_masked}` : ""}
          {result.denomination_cents ? ` · $${Math.round(result.denomination_cents / 100)}` : ""}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <ChannelPill label="SMS" status={result.sms_status} />
          <ChannelPill label="Email" status={result.email_status} />
        </div>
        {result.delivery_headline && (
          <div className="text-[11px] opacity-80 mt-2">{result.delivery_headline}</div>
        )}
        {showRetry && (
          <div className="mt-3">
            <Button
              size="sm"
              onClick={retryDelivery}
              disabled={retryBusy}
              data-testid="dealer-redeem-retry"
            >
              {retryBusy ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
              Retry send
            </Button>
            <div className="text-[10px] mt-1 opacity-75">
              Sends the SAME code — never a new one.
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto p-6" data-testid="dealer-redemption-page">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Redeem Gift Card</h1>
        <p className="text-slate-600 text-sm mt-1">
          Ask the customer for their claim ticket, type it below. The Amazon code
          is sent by SMS and email — you will not see the code, only the delivery status.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Ticket size={16} /> Enter claim ticket
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submitTicket} className="flex items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="ticket" className="text-[12px]">Ticket</Label>
              <Input
                id="ticket"
                value={ticket}
                onChange={(e) => setTicket(e.target.value)}
                placeholder="e.g. K7M2P9RN"
                autoComplete="off"
                autoFocus
                className="font-mono uppercase tracking-wider mt-1"
                data-testid="dealer-redeem-ticket-input"
                disabled={busy}
              />
            </div>
            <Button type="submit" disabled={busy || !ticket.trim()} data-testid="dealer-redeem-submit">
              {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
              Redeem
            </Button>
          </form>
          {error && (
            <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}
          {banner}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Search size={16} /> Resend a customer&apos;s code
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[12px] text-slate-500 mb-3">
            If the customer never got their SMS or email, look them up by phone or name and re-send
            the SAME code. Never mints a new card.
          </p>
          <form onSubmit={submitResend} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label htmlFor="resend-phone" className="text-[12px]">Phone</Label>
              <Input
                id="resend-phone"
                value={resendPhone}
                onChange={(e) => setResendPhone(e.target.value)}
                placeholder="(555) 555-5309"
                autoComplete="off"
                data-testid="dealer-resend-phone"
                disabled={resendBusy}
              />
            </div>
            <div>
              <Label htmlFor="resend-name" className="text-[12px]">…or partial name</Label>
              <Input
                id="resend-name"
                value={resendName}
                onChange={(e) => setResendName(e.target.value)}
                placeholder="Alex Morgan"
                autoComplete="off"
                data-testid="dealer-resend-name"
                disabled={resendBusy}
              />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" disabled={resendBusy} data-testid="dealer-resend-submit">
                {resendBusy ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                Resend code
              </Button>
            </div>
          </form>
          {resendError && (
            <div className="mt-3 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {resendError}
            </div>
          )}
          {resendResult && (() => {
            const v = computeBannerVariant(resendResult);
            return (
              <div className={`mt-3 rounded-md border p-3 ${TONE_CLS[v?.tone || "info"]}`}
                   data-testid="dealer-resend-result">
                <div className="text-[13px] font-semibold flex items-center gap-2">
                  {v?.icon} {v?.title}
                </div>
                <div className="text-[12px] mt-0.5">
                  {resendResult.customer_name} · {resendResult.customer_phone_masked}
                  {resendResult.denomination_cents ? ` · $${Math.round(resendResult.denomination_cents / 100)}` : ""}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <ChannelPill label="SMS" status={resendResult.sms_status} />
                  <ChannelPill label="Email" status={resendResult.email_status} />
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
